import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Download,
  ChevronUp,
  ChevronDown,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Truck,
  Package,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import financialService from '../../services/financialService';
import { useFinancialRefresh } from '../../contexts/FinancialRefreshContext';

// Helper to get date ranges
const getDateRanges = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Current month
  const startDate = new Date(currentYear, currentMonth, 1).toISOString().slice(0, 10);
  const endDate = new Date(currentYear, currentMonth + 1, 0).toISOString().slice(0, 10);

  return { startDate, endDate };
};

// Custom hook to detect mobile viewport
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const RevenueTab = ({ formatCurrency }) => {
  const isMobile = useIsMobile();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'revenue', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const { refreshTriggers } = useFinancialRefresh();
  const trucksPerPage = isMobile ? 5 : 10;

  // API data
  const [truckPerformance, setTruckPerformance] = useState([]);

  // Monthly revenue data
  const [monthlyRevenueData, setMonthlyRevenueData] = useState(null);
  const [monthlyLoading, setMonthlyLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Period filter for Revenue by Truck table
  const [periodFilter, setPeriodFilter] = useState('current-month');
  const [customYear, setCustomYear] = useState(new Date().getFullYear());
  const [customMonth, setCustomMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const storedCompany = window.localStorage.getItem('company');
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        setCompanyId(parsed?.id || null);
      }
    } catch (err) {
      console.warn('Unable to parse company from localStorage', err);
    }
  }, []);

  const fetchRevenueData = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      setError('Company context missing. Select a company to view revenue.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let startDate, endDate;

      // Calculate date range based on period filter
      if (periodFilter === 'current-month') {
        const dateRanges = getDateRanges();
        startDate = dateRanges.startDate;
        endDate = dateRanges.endDate;
      } else if (periodFilter === 'custom-month') {
        startDate = new Date(customYear, customMonth - 1, 1).toISOString().slice(0, 10);
        endDate = new Date(customYear, customMonth, 0).toISOString().slice(0, 10);
      } else if (periodFilter === 'year') {
        startDate = new Date(customYear, 0, 1).toISOString().slice(0, 10);
        endDate = new Date(customYear, 11, 31).toISOString().slice(0, 10);
      }

      console.log('📊 Fetching truck performance:', { periodFilter, startDate, endDate });

      // Fetch truck performance from Phase 6 API
      const trucks = await financialService.getTruckPerformance({
        startDate,
        endDate
      });

      console.log('✅ Received trucks:', trucks?.length || 0);
      setTruckPerformance(trucks || []);
    } catch (err) {
      console.error('Failed to load revenue data:', err);
      setError('Unable to load revenue data. Please try again.');
      setTruckPerformance([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, periodFilter, customYear, customMonth]);

  const fetchMonthlyRevenue = useCallback(async () => {
    if (!companyId) {
      setMonthlyLoading(false);
      return;
    }

    setMonthlyLoading(true);

    try {
      const monthlyData = await financialService.getMonthlyRevenueByTruck({
        year: selectedYear
      });

      setMonthlyRevenueData(monthlyData);
    } catch (err) {
      console.error('Failed to load monthly revenue data:', err);
      setMonthlyRevenueData(null);
    } finally {
      setMonthlyLoading(false);
    }
  }, [companyId, selectedYear]);

  useEffect(() => {
    if (!companyId) return;
    fetchRevenueData();
    fetchMonthlyRevenue();
  }, [companyId, fetchRevenueData, fetchMonthlyRevenue]);

  // Refresh data when triggered by financial changes
  useEffect(() => {
    if (!companyId) return;
    if (refreshTriggers.revenue > 0 || refreshTriggers.all > 0) {
      // Debounce refresh to avoid multiple rapid updates
      const timer = setTimeout(() => {
        fetchRevenueData();
        fetchMonthlyRevenue();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [refreshTriggers.revenue, refreshTriggers.all, companyId, fetchRevenueData, fetchMonthlyRevenue]);

  // Calculate totals
  const totals = useMemo(() => {
    return truckPerformance.reduce(
      (acc, truck) => ({
        revenue: acc.revenue + (truck.financial?.revenue || truck.revenue || 0),
        loadCount: acc.loadCount + (truck.transactions?.revenueCount || truck.loadCount || 0),
        totalMiles: acc.totalMiles + (truck.performance?.totalMiles || truck.totalMiles || 0),
        loadedMiles: acc.loadedMiles + (truck.performance?.loadedMiles || truck.loadedMiles || 0)
      }),
      { revenue: 0, loadCount: 0, totalMiles: 0, loadedMiles: 0 }
    );
  }, [truckPerformance]);

  // Calculate derived metrics
  const revenuePerLoad = totals.loadCount > 0 ? totals.revenue / totals.loadCount : 0;
  const revenuePerMile = totals.totalMiles > 0 ? totals.revenue / totals.totalMiles : 0;
  const deadheadPercentage = totals.totalMiles > 0
    ? ((totals.totalMiles - totals.loadedMiles) / totals.totalMiles) * 100
    : 0;

  // Sort truck data
  const sortedTrucks = useMemo(() => {
    let sortable = [...truckPerformance];

    if (sortConfig.key) {
      sortable.sort((a, b) => {
        let aValue, bValue;

        // Map sort keys to nested structure
        switch (sortConfig.key) {
          case 'truck':
            aValue = (a.vehicleNumber || a.vehicleId || a.truck || a.id || '').toString();
            bValue = (b.vehicleNumber || b.vehicleId || b.truck || b.id || '').toString();
            break;
          case 'revenue':
            aValue = a.financial?.revenue || a.revenue || 0;
            bValue = b.financial?.revenue || b.revenue || 0;
            break;
          case 'loadCount':
            aValue = a.transactions?.revenueCount || a.loadCount || 0;
            bValue = b.transactions?.revenueCount || b.loadCount || 0;
            break;
          case 'totalMiles':
            aValue = a.performance?.totalMiles || a.totalMiles || 0;
            bValue = b.performance?.totalMiles || b.totalMiles || 0;
            break;
          case 'loadedMiles':
            aValue = a.performance?.loadedMiles || a.loadedMiles || 0;
            bValue = b.performance?.loadedMiles || b.loadedMiles || 0;
            break;
          case 'deadhead':
            const aTotalMiles = a.performance?.totalMiles || a.totalMiles || 0;
            const aLoadedMiles = a.performance?.loadedMiles || a.loadedMiles || 0;
            aValue = aTotalMiles > 0 ? ((aTotalMiles - aLoadedMiles) / aTotalMiles) * 100 : 0;

            const bTotalMiles = b.performance?.totalMiles || b.totalMiles || 0;
            const bLoadedMiles = b.performance?.loadedMiles || b.loadedMiles || 0;
            bValue = bTotalMiles > 0 ? ((bTotalMiles - bLoadedMiles) / bTotalMiles) * 100 : 0;
            break;
          case 'revPerLoad':
            const aRevenue = a.financial?.revenue || a.revenue || 0;
            const aLoadCount = a.transactions?.revenueCount || a.loadCount || 0;
            aValue = aLoadCount > 0 ? aRevenue / aLoadCount : 0;

            const bRevenue = b.financial?.revenue || b.revenue || 0;
            const bLoadCount = b.transactions?.revenueCount || b.loadCount || 0;
            bValue = bLoadCount > 0 ? bRevenue / bLoadCount : 0;
            break;
          case 'revPerMile':
            const aRev = a.financial?.revenue || a.revenue || 0;
            const aMiles = a.performance?.totalMiles || a.totalMiles || 0;
            aValue = aMiles > 0 ? aRev / aMiles : 0;

            const bRev = b.financial?.revenue || b.revenue || 0;
            const bMiles = b.performance?.totalMiles || b.totalMiles || 0;
            bValue = bMiles > 0 ? bRev / bMiles : 0;
            break;
          default:
            aValue = a[sortConfig.key] || 0;
            bValue = b[sortConfig.key] || 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortable;
  }, [truckPerformance, sortConfig]);

  const paginatedTrucks = useMemo(() => {
    const startIndex = (currentPage - 1) * trucksPerPage;
    return sortedTrucks.slice(startIndex, startIndex + trucksPerPage);
  }, [sortedTrucks, currentPage]);

  const totalPages = Math.ceil(sortedTrucks.length / trucksPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const exportToCSV = () => {
    const headers = ['Truck', 'Revenue', 'Loads', 'Total Miles', 'Loaded Miles', 'Deadhead %', 'Revenue/Load', 'Revenue/Mile'];
    const rows = sortedTrucks.map(truck => {
      const revenue = truck.financial?.revenue || truck.revenue || 0;
      const loadCount = truck.transactions?.revenueCount || truck.loadCount || 0;
      const totalMiles = truck.performance?.totalMiles || truck.totalMiles || 0;
      const loadedMiles = truck.performance?.loadedMiles || truck.loadedMiles || 0;

      const deadhead = totalMiles > 0
        ? ((totalMiles - loadedMiles) / totalMiles) * 100
        : 0;
      const revPerLoad = loadCount > 0 ? revenue / loadCount : 0;
      const revPerMile = totalMiles > 0 ? revenue / totalMiles : 0;

      return [
        truck.vehicleNumber || truck.vehicleId || truck.truck || truck.id,
        revenue,
        loadCount,
        totalMiles,
        loadedMiles,
        deadhead.toFixed(1),
        revPerLoad.toFixed(2),
        revPerMile.toFixed(2)
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'revenue-by-truck.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ChevronUp className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="w-4 h-4 text-gray-600" /> :
      <ChevronDown className="w-4 h-4 text-gray-600" />;
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', isPercentage = false }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6">
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">{title}</p>
          <p className={`text-lg sm:text-xl md:text-2xl font-bold truncate ${color === 'blue' ? 'text-blue-600' : color === 'green' ? 'text-green-600' : 'text-gray-900'}`}>
            {isPercentage ? `${value.toFixed(1)}%` : formatCurrency(value)}
          </p>
        </div>
        {Icon && <Icon className={`w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 flex-shrink-0 ${color === 'blue' ? 'text-blue-600' : color === 'green' ? 'text-green-600' : 'text-gray-400'}`} />}
      </div>
    </div>
  );

  // Mobile card component for truck data
  const MobileTruckCard = ({ truck }) => {
    const deadhead = truck.totalMiles > 0
      ? ((truck.totalMiles - (truck.loadedMiles || 0)) / truck.totalMiles) * 100
      : 0;
    const revPerLoad = (truck.loadCount || 0) > 0 ? truck.revenue / truck.loadCount : 0;
    const revPerMile = (truck.totalMiles || 0) > 0 ? truck.revenue / truck.totalMiles : 0;

    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        {/* Header with truck name and revenue */}
        <div className="flex items-center justify-between">
          <span className="font-semibold text-gray-900">{truck.truck || truck.id}</span>
          <span className="text-lg font-bold text-blue-600">{formatCurrency(truck.revenue || 0)}</span>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-gray-500">Loads</span>
            <p className="font-medium text-gray-900">{truck.loadCount || 0}</p>
          </div>
          <div>
            <span className="text-gray-500">Total Miles</span>
            <p className="font-medium text-gray-900">{(truck.totalMiles || 0).toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-500">Rev/Load</span>
            <p className="font-medium text-gray-900">{formatCurrency(revPerLoad)}</p>
          </div>
          <div>
            <span className="text-gray-500">Rev/Mile</span>
            <p className="font-medium text-gray-900">${revPerMile.toFixed(2)}</p>
          </div>
        </div>

        {/* Deadhead badge */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <span className="text-sm text-gray-500">Deadhead</span>
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
            deadhead > 20 ? 'bg-red-100 text-red-800' : deadhead > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
          }`}>
            {deadhead.toFixed(1)}%
          </span>
        </div>
      </div>
    );
  };

  // Mobile totals card
  const MobileTotalsCard = () => (
    <div className="bg-gray-100 border-2 border-gray-300 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="font-bold text-gray-900">TOTALS</span>
        <span className="text-lg font-bold text-blue-600">{formatCurrency(totals.revenue)}</span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <span className="text-gray-500">Loads</span>
          <p className="font-bold text-gray-900">{totals.loadCount}</p>
        </div>
        <div>
          <span className="text-gray-500">Total Miles</span>
          <p className="font-bold text-gray-900">{totals.totalMiles.toLocaleString()}</p>
        </div>
        <div>
          <span className="text-gray-500">Rev/Load</span>
          <p className="font-bold text-gray-900">{formatCurrency(revenuePerLoad)}</p>
        </div>
        <div>
          <span className="text-gray-500">Rev/Mile</span>
          <p className="font-bold text-gray-900">${revenuePerMile.toFixed(2)}</p>
        </div>
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-gray-300">
        <span className="text-sm font-medium text-gray-600">Avg Deadhead</span>
        <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${
          deadheadPercentage > 20 ? 'bg-red-100 text-red-800' : deadheadPercentage > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
        }`}>
          {deadheadPercentage.toFixed(1)}%
        </span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 md:p-6 animate-pulse">
              <div className="h-3 sm:h-4 bg-gray-200 rounded mb-2 sm:mb-3"></div>
              <div className="h-6 sm:h-8 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 animate-pulse">
          <div className="h-5 sm:h-6 bg-gray-200 rounded mb-4 w-1/2 sm:w-1/3"></div>
          <div className="h-64 sm:h-80 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-6">
        <div className="flex items-center justify-between text-red-700">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5" />
            <p>{error}</p>
          </div>
          <button type="button" className="text-sm font-medium underline" onClick={fetchRevenueData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Render Monthly Revenue Table (shared between empty state and normal state)
  const renderMonthlyRevenueTable = () => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mt-6">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Monthly Revenue by Truck</h3>
            <p className="text-sm text-gray-600 mt-1">Year-to-date revenue breakdown by truck and month</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {[...Array(5)].map((_, i) => {
                const year = new Date().getFullYear() - i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
            <button
              onClick={() => {
                if (!monthlyRevenueData || !monthlyRevenueData.trucks) return;
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const headers = ['Truck', ...months, 'Total', 'Average'];
                const csvContent = [
                  headers.join(','),
                  ...(monthlyRevenueData.trucks || []).map(truck => [
                    truck.vehicleNumber,
                    ...truck.monthlyRevenue.map(rev => rev.toFixed(2)),
                    truck.totalRevenue.toFixed(2),
                    truck.averageMonthlyRevenue.toFixed(2)
                  ].join(','))
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `monthly-revenue-${selectedYear}.csv`;
                a.click();
                window.URL.revokeObjectURL(url);
              }}
              disabled={!monthlyRevenueData || !monthlyRevenueData.trucks || monthlyLoading}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      </div>

      {monthlyLoading ? (
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
          <div className="h-96 bg-gray-200 rounded"></div>
        </div>
      ) : monthlyRevenueData && monthlyRevenueData.trucks && monthlyRevenueData.trucks.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                  Truck #
                </th>
                {(monthlyRevenueData.months || []).map((month, index) => (
                  <th
                    key={month.period}
                    className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {month.monthName.split(' ')[0]}
                  </th>
                ))}
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  Total
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                  Avg/Month
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {monthlyRevenueData.trucks.map((truck) => (
                <tr key={truck.vehicleId} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    {truck.vehicleNumber}
                  </td>
                  {truck.monthlyRevenue.map((revenue, monthIndex) => (
                    <td
                      key={monthIndex}
                      className={`px-4 py-3 whitespace-nowrap text-sm text-right ${
                        revenue > 0 ? 'text-gray-900' : 'text-gray-400'
                      }`}
                    >
                      {revenue > 0 ? formatCurrency(revenue) : '-'}
                    </td>
                  ))}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-semibold text-blue-600 bg-blue-50">
                    {formatCurrency(truck.totalRevenue)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 bg-blue-50">
                    {formatCurrency(truck.averageMonthlyRevenue)}
                  </td>
                </tr>
              ))}

              {/* Totals Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-gray-100">
                  TOTALS
                </td>
                {(monthlyRevenueData.totals?.monthlyTotals || []).map((total, monthIndex) => (
                  <td
                    key={monthIndex}
                    className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900"
                  >
                    {total > 0 ? formatCurrency(total) : '-'}
                  </td>
                ))}
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600 bg-blue-100">
                  {formatCurrency(monthlyRevenueData.totals?.yearTotal || 0)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 bg-blue-100">
                  {formatCurrency(monthlyRevenueData.totals?.averageMonthly || 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-6">
          <p className="text-sm text-gray-600">No monthly revenue data available for {selectedYear}.</p>
        </div>
      )}
    </div>
  );

  // Helper to format period for display
  const getPeriodDisplayText = () => {
    if (periodFilter === 'current-month') {
      return 'the current month';
    } else if (periodFilter === 'custom-month') {
      return new Date(customYear, customMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (periodFilter === 'year') {
      return `year ${customYear}`;
    }
    return 'the selected period';
  };

  if (truckPerformance.length === 0) {
    return (
      <div className="space-y-6">
        {/* Summary Cards with zeros */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Revenue"
            value={0}
            icon={DollarSign}
            color="blue"
          />
          <StatCard
            title="Revenue Per Load"
            value={0}
            icon={Package}
            color="green"
          />
          <StatCard
            title="Revenue Per Mile"
            value={0}
            icon={TrendingUp}
          />
          <StatCard
            title="Deadhead %"
            value={0}
            icon={Truck}
            isPercentage
          />
        </div>

        {/* Revenue by Truck Table with Empty State */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Revenue by Truck</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {periodFilter === 'current-month' && 'Current month performance'}
                  {periodFilter === 'custom-month' && `${getPeriodDisplayText()} performance`}
                  {periodFilter === 'year' && `${customYear} year performance`}
                </p>
              </div>
              <button
                disabled
                className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-500 rounded-lg cursor-not-allowed text-sm"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>

            {/* Period Filter - Keep it visible */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center pt-2 border-t border-gray-100">
              <label className="text-sm font-medium text-gray-700 min-w-[60px]">Period:</label>
              <div className="flex flex-wrap gap-3 items-center">
                <select
                  value={periodFilter}
                  onChange={(e) => setPeriodFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  <option value="current-month">Current Month</option>
                  <option value="custom-month">Specific Month</option>
                  <option value="year">Full Year</option>
                </select>

                {periodFilter === 'custom-month' && (
                  <>
                    <select
                      value={customMonth}
                      onChange={(e) => setCustomMonth(parseInt(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                        <option key={index + 1} value={index + 1}>
                          {month}
                        </option>
                      ))}
                    </select>
                    <select
                      value={customYear}
                      onChange={(e) => setCustomYear(parseInt(e.target.value))}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                    >
                      {[...Array(5)].map((_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </>
                )}

                {periodFilter === 'year' && (
                  <select
                    value={customYear}
                    onChange={(e) => setCustomYear(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                )}
              </div>
            </div>
          </div>

          {/* Empty State Message */}
          <div className="p-12 text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No Revenue Data for {getPeriodDisplayText()}
            </h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
              There are no delivered loads or revenue transactions recorded for {getPeriodDisplayText()}.
              {periodFilter !== 'current-month' && ' Try selecting a different time period or '}
              {periodFilter === 'current-month' && ' '}
              Once loads are delivered and transactions are posted, revenue metrics will appear here.
            </p>
            {periodFilter !== 'current-month' && (
              <button
                onClick={() => setPeriodFilter('current-month')}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
              >
                <span>View Current Month</span>
              </button>
            )}
          </div>
        </div>

        {/* Monthly Revenue by Truck Table - Still render */}
        {renderMonthlyRevenueTable()}
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards - 2x2 grid on mobile, 4 columns on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <StatCard
          title="Total Revenue"
          value={totals.revenue}
          icon={DollarSign}
          color="blue"
        />
        <StatCard
          title="Rev/Load"
          value={revenuePerLoad}
          icon={Package}
          color="green"
        />
        <StatCard
          title="Rev/Mile"
          value={revenuePerMile}
          icon={TrendingUp}
        />
        <StatCard
          title="Deadhead %"
          value={deadheadPercentage}
          icon={Truck}
          isPercentage
        />
      </div>

      {/* Revenue by Truck Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Revenue by Truck</h3>
              <p className="text-sm text-gray-600 mt-1">
                {periodFilter === 'current-month' && 'Current month performance'}
                {periodFilter === 'custom-month' && `${new Date(customYear, customMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} performance`}
                {periodFilter === 'year' && `${customYear} year performance`}
              </p>
            </div>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center space-x-2 px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors duration-200 text-xs sm:text-sm touch-manipulation"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>

          {/* Period Filter */}
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center pt-2 border-t border-gray-100">
            <label className="text-sm font-medium text-gray-700 min-w-[60px]">Period:</label>
            <div className="flex flex-wrap gap-3 items-center">
              <select
                value={periodFilter}
                onChange={(e) => setPeriodFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="current-month">Current Month</option>
                <option value="custom-month">Specific Month</option>
                <option value="year">Full Year</option>
              </select>

              {periodFilter === 'custom-month' && (
                <>
                  <select
                    value={customMonth}
                    onChange={(e) => setCustomMonth(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, index) => (
                      <option key={index + 1} value={index + 1}>
                        {month}
                      </option>
                    ))}
                  </select>
                  <select
                    value={customYear}
                    onChange={(e) => setCustomYear(parseInt(e.target.value))}
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  >
                    {[...Array(5)].map((_, i) => {
                      const year = new Date().getFullYear() - i;
                      return (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      );
                    })}
                  </select>
                </>
              )}

              {periodFilter === 'year' && (
                <select
                  value={customYear}
                  onChange={(e) => setCustomYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {[...Array(5)].map((_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {[
                  { key: 'truck', label: 'Truck #' },
                  { key: 'revenue', label: 'Revenue' },
                  { key: 'loadCount', label: 'Loads' },
                  { key: 'totalMiles', label: 'Total Miles' },
                  { key: 'loadedMiles', label: 'Loaded Miles' },
                  { key: 'deadhead', label: 'Deadhead %' },
                  { key: 'revPerLoad', label: 'Revenue/Load' },
                  { key: 'revPerMile', label: 'Revenue/Mile' }
                ].map(({ key, label }) => (
                  <th
                    key={key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort(key)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{label}</span>
                      <SortIcon column={key} />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedTrucks.map((truck) => {
                // Extract data from nested structure
                const revenue = truck.financial?.revenue || truck.revenue || 0;
                const loadCount = truck.transactions?.revenueCount || truck.loadCount || 0;
                const totalMiles = truck.performance?.totalMiles || truck.totalMiles || 0;
                const loadedMiles = truck.performance?.loadedMiles || truck.loadedMiles || 0;

                const deadhead = totalMiles > 0
                  ? ((totalMiles - loadedMiles) / totalMiles) * 100
                  : 0;
                const revPerLoad = loadCount > 0 ? revenue / loadCount : 0;
                const revPerMile = totalMiles > 0 ? revenue / totalMiles : 0;

                return (
                  <tr key={truck.vehicleId || truck.id} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {truck.vehicleNumber || truck.vehicleId || truck.truck || truck.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                      {formatCurrency(revenue)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {loadCount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {totalMiles.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {loadedMiles.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        deadhead > 20 ? 'bg-red-100 text-red-800' : deadhead > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                      }`}>
                        {deadhead.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(revPerLoad)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${revPerMile.toFixed(2)}
                    </td>
                  </tr>
                );
              })}

              {/* Totals Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  TOTALS
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                  {formatCurrency(totals.revenue)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {totals.loadCount}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {totals.totalMiles.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {totals.loadedMiles.toLocaleString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                  <span className={`inline-flex px-2 py-1 text-xs font-bold rounded-full ${
                    deadheadPercentage > 20 ? 'bg-red-100 text-red-800' : deadheadPercentage > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                  }`}>
                    {deadheadPercentage.toFixed(1)}%
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {formatCurrency(revenuePerLoad)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  ${revenuePerMile.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Pagination - mobile-friendly */}
        {totalPages > 1 && (
          <div className="px-4 sm:px-6 py-3 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs sm:text-sm text-gray-700 order-2 sm:order-1">
                {isMobile
                  ? `${currentPage} of ${totalPages}`
                  : `Showing ${((currentPage - 1) * trucksPerPage) + 1} to ${Math.min(currentPage * trucksPerPage, sortedTrucks.length)} of ${sortedTrucks.length}`
                }
              </div>
              <div className="flex items-center gap-2 order-1 sm:order-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 sm:px-3 sm:py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 active:bg-gray-300 transition-colors duration-200 touch-manipulation"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Previous</span>
                </button>
                <span className="text-sm text-gray-700 px-2 sm:px-0">
                  {isMobile ? '' : `Page ${currentPage} of ${totalPages}`}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 sm:px-3 sm:py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 active:bg-gray-300 transition-colors duration-200 touch-manipulation"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-5 h-5 sm:hidden" />
                  <span className="hidden sm:inline">Next</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Monthly Revenue by Truck Table */}
      {renderMonthlyRevenueTable()}
    </div>
  );
};

export default RevenueTab;
